if (!customElements.get('cyber-monday-slider')) {
  customElements.define(
    'cyber-monday-slider',
    class CyberMondaySlider extends HTMLElement {
      constructor() {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.progressBar = this.querySelector('[data-slider-progress-bar]');
        this.progressBarIndicator = this.querySelector('[data-slider-progress-bar-indicator]');
        this.paginationContainer = this.querySelector('.slider-pagination-container');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        if (!this.slider || !this.nextButton) return;

        const dayRange = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'weekend'];
        const featuredDay = this.dataset.featuredDay;
        this.featuredSlideIndex = dayRange.indexOf(featuredDay);
        this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);

        this.centerOrClampToEdges(this.sliderItemsToShow[this.featuredSlideIndex]);

        const resizeObserver = new ResizeObserver((entries) => this.initPages());
        resizeObserver.observe(this.slider);

        this.slider.addEventListener('scroll', this.update.bind(this));
        this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
      }

      initPages() {
        if (this.sliderItemsToShow.length <= 2) {
          this.nextButton.setAttribute('disabled', 'disabled');
          this.prevButton.setAttribute('disabled', 'disabled');
          this.progressBar?.classList.add('hidden');
          return;
        }

        this.progressBar?.classList.remove('hidden');

        this.sliderItemOffset =
          this.dataset.featuredDay === 'monday'
            ? this.sliderItemsToShow[2].offsetLeft - this.sliderItemsToShow[1].offsetLeft
            : this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

        this.slidesPerPage = Math.floor(
          (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
        );
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
        this.update();
      }

      resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
      }

      update() {
        // Temporarily prevents unneeded updates resulting from variant changes
        // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
        if (!this.slider || !this.nextButton) return;

        const previousPage = this.currentPage;

        // Handle how the slider tracks what is considered the current page based on if:
        // * The slider is right aligned (weekend)
        // * The slider is center aligned with other slides fully visible (desktop default)
        // * The slider is center aligned with no other slides fully visible (mobile)
        const pageOffset = window.matchMedia('(min-width: 768px)').matches
          ? this.dataset.featuredDay === 'weekend'
            ? 3
            : 1
          : 0;

        this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + pageOffset;

        if (this.progressBar && this.progressBarIndicator) {
          if (this.totalPages === 1) {
            this.progressBar?.classList.add('hidden');
          }

          // Need to do a bunch of fiddling due to bumpers being added or removed
          const totalPagesOffset = window.matchMedia('(min-width: 768px)').matches
            ? this.dataset.featuredDay === 'weekend'
              ? -1
              : 1
            : 1;

          const currentPageOffset = window.matchMedia('(min-width: 768px)').matches
            ? this.dataset.featuredDay === 'weekend'
              ? 3
              : 1
            : 0;

          this.progressBarIndicator.style.width = `${(1 / (this.totalPages + totalPagesOffset)) * 100}%`;
          this.progressBarIndicator.style.left = `${
            ((this.currentPage - currentPageOffset) / (this.totalPages + totalPagesOffset)) * 100
          }%`;
        }

        if (this.currentPage != previousPage) {
          const targetId = this.sliderItemsToShow[this.currentPage - 1].id;
          this.sliderItemsToShow.forEach((slide) => {
            if (targetId === slide.id) {
              slide.classList.add('cyber-featured-slide--featured');
            } else {
              slide.classList.remove('cyber-featured-slide--featured');
            }
          });
        }

        const first = this.sliderItemsToShow[0];
        const last = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];

        // Left button
        if (this.isSlideFullyVisible(first) && this.slider.scrollLeft === 0) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }

        // Right button
        const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;
        if (this.isSlideFullyVisible(last) && this.slider.scrollLeft + 1 >= maxScrollLeft) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }

        if (this.paginationContainer) {
          this.paginationContainer.classList.toggle('prev-active', !this.prevButton.disabled);
        }
      }

      isSlideFullyVisible(element) {
        const slider = this.slider;

        const sliderLeft = slider.scrollLeft;
        const sliderRight = slider.scrollLeft + slider.clientWidth;

        const elemLeft = element.offsetLeft;
        const elemRight = element.offsetLeft + element.offsetWidth;

        return elemLeft >= sliderLeft && elemRight <= sliderRight;
      }

      onButtonClick(event) {
        event.preventDefault();
        const isNext = event.currentTarget.name === 'next';

        if (
          (window.matchMedia('(min-width: 768px)').matches && this.dataset.featuredDay === 'weekend') ||
          (window.matchMedia('(min-width: 768px)').matches && this.dataset.featuredDay === 'monday')
        ) {
          const step = 1;
          this.slideScrollPosition = this.slider.scrollLeft + (isNext ? step : -step) * this.sliderItemOffset;

          this.setSlidePosition(this.slideScrollPosition);
          return;
        }

        this.handleCenteredItemsNav(isNext);
      }

      handleCenteredItemsNav(isNext) {
        const sliderItems = this.querySelectorAll('[id^="Slide-"]');
        const slides = Array.from(sliderItems);
        const featuredIndex = slides.findIndex((slide) => slide.classList.contains('cyber-featured-slide--featured'));

        if (featuredIndex === -1) return;

        const targetIndex = isNext ? featuredIndex + 1 : featuredIndex - 1;

        if (targetIndex < 0 || targetIndex >= slides.length) return;

        this.centerOrClampToEdges(slides[targetIndex]);
      }

      setSlidePosition(position) {
        this.slider.scrollTo({
          left: position,
          behavior: 'smooth',
        });
      }

      centerOrClampToEdges(slideElement) {
        const containerWidth = this.slider.clientWidth;
        const contentWidth = this.slider.scrollWidth;

        // Maximum scrollable distance
        const maxScrollLeft = contentWidth - containerWidth;

        // If content fits inside container: don't scroll at all
        if (contentWidth <= containerWidth) {
          this.setSlidePosition(0);
          return;
        }

        // Try to center the slide
        const slideCenter = slideElement.offsetLeft + slideElement.clientWidth / 2;

        let targetScrollLeft = slideCenter - containerWidth / 2;

        // Clamp between 0 and maxScrollLeft
        targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

        if (window.matchMedia('(min-width: 768px)').matches && this.dataset.featuredDay === 'weekend') {
          targetScrollLeft = maxScrollLeft;
        }

        this.setSlidePosition(targetScrollLeft);
      }
    }
  );
}
